import { Component, computed, ElementRef, inject, signal } from '@angular/core';
import { of, switchMap } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QRCodeComponent } from 'angularx-qrcode';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CreateEasypassRequest, EasypassDto, EasypassPaymentBehavior, IssueEasypassExitTicketsRequest, IssueEasypassForPlateRequest, OrganizationDto, OrganizationService, ParkingProcessListItemDto, ParkingProcessService, ParkingSpaceDto, ParkingTypeService } from '../../../../api/gen';
import { OrgCostCenterService } from '../../../../api/gen/api/orgCostCenter.service';
import { environment } from '../../../../environments/environment';
import { apiErrorMessage, JJamesAutocompleteCreateComponent, JJamesDropdownOption, MAX_PLATE_LENGTH, normalizePlate, sanitizePlateInput } from 'shared-lib';
import { openExitTicketPrint } from '../exit-ticket-print/exit-ticket-print-dialog.component';

export interface IssueEasypassDialogData {
  parkingSpace: ParkingSpaceDto;
  parkingOperatorId: string | undefined;
}

/** The issue forms, one per Easypass mode a space can be switched on for (Quick has its own dialog). */
export type IssueEasypassMode = 'simple' | 'advanced' | 'exitTickets';

/**
 * Most exit tickets one batch may print. Mirrors the backend's `ParkingRight:EasypassExitTickets:
 * MaxCodesPerBatch` default; the backend still enforces its own value and names it in the error.
 */
export const MAX_EXIT_TICKETS_PER_BATCH = 20;

/** The modes switched on for a space, in the order the toggle shows them. */
export function enabledIssueModes(space: ParkingSpaceDto | null | undefined): IssueEasypassMode[] {
  const modes: IssueEasypassMode[] = [];
  if (space?.easypassSimpleEnabled) modes.push('simple');
  if (space?.easypassMultiQrEnabled) modes.push('exitTickets');
  if (space?.easypassAdvancedEnabled) modes.push('advanced');
  return modes;
}

@Component({
  selector: 'app-issue-easypass-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    QRCodeComponent,
    JJamesAutocompleteCreateComponent,
    TranslatePipe
  ],
  providers: [DatePipe],
  templateUrl: './issue-easypass-dialog.component.html',
  styleUrl: './issue-easypass-dialog.component.scss'
})
export class IssueEasypassDialogComponent {
  private fb = inject(FormBuilder);
  private parkingTypeService = inject(ParkingTypeService);
  private parkingProcessService = inject(ParkingProcessService);
  private translate = inject(TranslateService);
  private organizationService = inject(OrganizationService);
  private orgCostCenterService = inject(OrgCostCenterService);
  private dialogRef = inject(MatDialogRef<IssueEasypassDialogComponent>);
  private data = inject<IssueEasypassDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);
  private host = inject(ElementRef<HTMLElement>);
  private dialog = inject(MatDialog);

  readonly PaymentBehavior = EasypassPaymentBehavior;
  readonly maxExitTickets = MAX_EXIT_TICKETS_PER_BATCH;

  /**
   * Simple mode (default) is the desk case: the visitor is standing there, so the operator types
   * their plate, the window, who is billed — and the pass is issued AND redeemed in one step. There
   * is no QR to hand over and nobody is sent to the parker app.
   *
   * Advanced is the shared-invitation form, unchanged: a token many guests redeem themselves.
   *
   * Exit tickets print N single-use QR codes, each worth one free exit, billed to the space's
   * organization. Only the modes switched on for the space are offered.
   */
  readonly modes = enabledIssueModes(this.data.parkingSpace);
  mode = signal<IssueEasypassMode>(this.modes[0] ?? 'simple');

  /**
   * What simple mode fixes so the desk is not asked about it. Unlimited re-entries (a visitor may
   * leave and come back during their window), a single plate, every zone of the space, org-billed.
   * Organization and cost center are NOT here — they are asked for in both modes.
   */
  private static readonly SimpleModeDefaults = {
    maxRedemptions: 1,
    isUnlimited: false,
    allowedEntries: 0,
    paymentBehavior: EasypassPaymentBehavior.OrgPays,
    selectedZones: [] as string[]
  };

  setMode(mode: IssueEasypassMode): void {
    this.mode.set(mode);

    // Title is the shared invitation's label and is required there; the desk form does not ask for
    // one (the backend names the pass after the plate), so the validator cannot outlive the mode —
    // left in place it blocks a simple submit over a field that is not on screen.
    const title = this.form.get('title');
    const plate = this.form.get('licensePlate');
    const validUntil = this.form.get('validUntil');
    const codeCount = this.form.get('codeCount');

    if (mode === 'simple') {
      this.form.patchValue(IssueEasypassDialogComponent.SimpleModeDefaults);
      this.form.get('maxRedemptions')?.enable();
      this.form.get('increaseUntil')?.enable();
      title?.clearValidators();
      plate?.setValidators([Validators.required]);
    } else {
      title?.setValidators([Validators.required]);
      plate?.clearValidators();
    }

    // Valid-until has no default on purpose: the operator states it for every batch.
    if (mode === 'exitTickets') {
      validUntil?.setValidators([Validators.required]);
      codeCount?.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(MAX_EXIT_TICKETS_PER_BATCH),
        Validators.pattern(/^\d+$/)
      ]);
    } else {
      validUntil?.clearValidators();
      codeCount?.clearValidators();
    }

    title?.updateValueAndValidity({ emitEvent: false });
    plate?.updateValueAndValidity({ emitEvent: false });
    validUntil?.updateValueAndValidity({ emitEvent: false });
    codeCount?.updateValueAndValidity({ emitEvent: false });
  }

  /** The selected organization's own cost centers — the picker's data source. Empty until one is picked. */
  costCenterOptions = signal<JJamesDropdownOption[]>([]);
  costCenterLoading = signal(false);

  /**
   * Organizations this event could be billed to: the ones with access to this parking space or a zone of it, plus
   * the organization that governs the site itself (see addGoverningOrganization). Listing every
   * organization would let an operator attribute an event to a tenant with no parking here, and the
   * bill would go to someone with no way to recognise it.
   */
  organizations = signal<OrganizationDto[]>([]);
  orgsLoading = signal(false);

  /**
   * The organization picker's options. The leading '' entry is "no organization" — picking it clears
   * the selection, since the search box has no other way back to the operator's own event.
   */
  organizationOptions = computed<JJamesDropdownOption[]>(() => [
    { label: this.translate.instant('easypass.noOrganization'), value: '' },
    ...this.organizations().map(o => ({ label: o.name ?? '', value: o.id! }))
  ]);

  /**
   * The plate's running stay in THIS space, when it has one. A visitor who drove in on a short-term
   * ticket is already being charged by the minute, so the pass has to start at their entry time
   * rather than at the moment the operator gets round to issuing it.
   */
  activeStay = signal<ParkingProcessListItemDto | null>(null);
  plateLookupLoading = signal(false);

  isLoading = signal(false);
  issued = signal<EasypassDto | null>(null);
  copied = signal(false);
  tokenCopied = signal(false);
  downloadError = signal<string | null>(null);
  showAdminSection = signal(false);

  // Quick entries options
  entryOptions = [
    { value: 0, label: 'Unlimited re-entries', hint: 'Visitors can enter & exit freely during the valid window' },
    { value: 1, label: '1 entry (Single use)', hint: 'Ticket expires once vehicle exits' },
    { value: 2, label: '2 entries', hint: 'Allows two visits' },
    { value: 3, label: '3 entries', hint: 'Allows three visits' },
    { value: 5, label: '5 entries', hint: 'Allows five visits' },
    { value: 10, label: '10 entries', hint: 'Allows up to ten visits' }
  ];

  form: FormGroup = this.fb.group({
    licensePlate: [''],
    title: ['', [Validators.required]],
    description: [''],
    maxRedemptions: [1, [Validators.min(1)]],
    isUnlimited: [false],
    allowedEntries: [0],
    organizationId: [null as string | null],
    paymentBehavior: [EasypassPaymentBehavior.OrgPays],
    validFrom: [null],
    validTo: [null],
    increaseUntil: [null],
    selectedZones: [[] as string[]],
    costCenterId: [null as string | null],
    email: [''],
    phone: [''],
    comment: [''],
    validUntil: [null as string | null],
    codeCount: [null as number | null]
  });

  constructor() {
    this.setMode(this.mode());
    this.loadOrganizations();
    this.form.get('organizationId')?.valueChanges.subscribe((organizationId: string | null) => {
      // The old selection belonged to the organization that was just replaced, so it cannot stay.
      this.form.patchValue({ costCenterId: null }, { emitEvent: false });
      this.loadCostCenters(organizationId);
    });
  }

  /**
   * Loads the cost-center picker's options: only the ones belonging to the selected organization.
   * The endpoint merges that organization's rows with the global (org-less) ones, which are filtered
   * out here — a global cost center says nothing about who is billed, so it must not be offered.
   * With no organization selected there is nothing to pick and the picker stays empty.
   */
  private loadCostCenters(organizationId: string | null): void {
    if (!organizationId) {
      this.costCenterOptions.set([]);
      this.costCenterLoading.set(false);
      return;
    }

    this.costCenterLoading.set(true);
    this.orgCostCenterService.orgCostCentersGet(organizationId).subscribe({
      next: (result) => {
        this.costCenterOptions.set((result ?? [])
          .filter(c => c.organizationId === organizationId)
          .map(c => ({ label: c.name, value: c.id! })));
        this.costCenterLoading.set(false);
      },
      error: () => {
        this.costCenterOptions.set([]);
        this.costCenterLoading.set(false);
      }
    });
  }

  onOrganizationSelected(organizationId: string): void {
    this.form.patchValue({ organizationId: organizationId || null });
  }

  onCostCenterSelected(costCenterId: string): void {
    this.form.patchValue({ costCenterId });
  }

  onCostCenterCreateNew(name: string): void {
    const organizationId = this.form.get('organizationId')?.value ?? null;
    // Without an organization the new row would be a global one, which the picker never shows again.
    if (!organizationId) return;

    this.costCenterLoading.set(true);
    this.orgCostCenterService.orgCostCentersPost({ organizationId, name }).subscribe({
      next: (created) => {
        this.costCenterOptions.update(opts => [...opts, { label: created.name, value: created.id! }]);
        this.form.patchValue({ costCenterId: created.id });
        this.costCenterLoading.set(false);
      },
      error: (error) => {
        this.costCenterLoading.set(false);
        this.snackBar.open(apiErrorMessage(error, 'Failed to add cost center'), 'Close', { duration: 5000 });
      }
    });
  }

  get parkingSpace(): ParkingSpaceDto {
    return this.data.parkingSpace;
  }

  /**
   * Loads the organizations with access to this parking space or a zone of it. The space is fixed for the life of
   * the dialog, so this runs once.
   *
   * On failure the list stays empty and the field simply offers no organization — the event is then
   * the operator's own, which is a valid outcome. Guessing a wider list would be worse: the operator
   * cannot tell a plausible-looking wrong tenant from the right one, and the event's cost would be
   * attributed to whoever was picked.
   *
   * The picker filters client-side, so it needs every holder: a site with more holders than the first
   * page refetches once with the reported total, rather than silently hiding the rest.
   */
  private loadOrganizations(): void {
    const spaceId = this.parkingSpace?.id;
    if (!spaceId) return;

    this.orgsLoading.set(true);
    this.organizationService.organizationsByParkingSpaceParkingSpaceIdGet(spaceId, 0, 500).pipe(
      switchMap(first => (first?.totalCount ?? 0) > (first?.items?.length ?? 0)
        ? this.organizationService.organizationsByParkingSpaceParkingSpaceIdGet(spaceId, 0, first.totalCount)
        : of(first))
    ).subscribe({
      next: result => this.addGoverningOrganization(result?.items ?? []),
      error: () => this.addGoverningOrganization([])
    });
  }

  /**
   * Adds the organization that governs the site itself — ParkingSpace.externalOrganizationId — to the
   * pool holders, and publishes the combined list.
   *
   * A campus operator holds no pool zone of its own, so the by-space query never returns it, yet it is
   * exactly who an event there is billed to: at a JKU space the list has to offer JKU. It is listed
   * first because at a site it governs it is the likeliest payer.
   *
   * A space with no governing organization adds nothing — the list is then the pool holders alone,
   * which is the behaviour every space had before this.
   */
  private addGoverningOrganization(poolHolders: OrganizationDto[]): void {
    const governingId = this.parkingSpace?.externalOrganizationId;
    if (!governingId || poolHolders.some(o => o.id === governingId)) {
      this.organizations.set(poolHolders);
      this.orgsLoading.set(false);
      return;
    }

    this.organizationService.organizationsByIdsPost(undefined, undefined, [governingId]).subscribe({
      next: orgs => {
        const governing = (orgs ?? []).find(o => o.id === governingId);
        this.organizations.set(governing ? [governing, ...poolHolders] : poolHolders);
        this.orgsLoading.set(false);
      },
      // The pool holders are still a usable list; a governor that cannot be read is not an error.
      error: () => {
        this.organizations.set(poolHolders);
        this.orgsLoading.set(false);
      }
    });
  }

  availableZones() {
    return this.parkingSpace?.parkingZones || [];
  }

  get redeemLink(): string {
    const token = this.issued()?.token;
    return token ? `${environment.parkerUrl}/easypass/redeem/${token}` : '';
  }

  isUnlimitedCapacity(): boolean {
    return !!this.form.get('isUnlimited')?.value;
  }

  setCapacityMode(unlimited: boolean): void {
    this.form.patchValue({ isUnlimited: unlimited });
    const maxControl = this.form.get('maxRedemptions');
    const increaseControl = this.form.get('increaseUntil');
    if (unlimited) {
      maxControl?.disable();
      increaseControl?.disable();
    } else {
      maxControl?.enable();
      increaseControl?.enable();
      if (!maxControl?.value || maxControl.value < 1) {
        maxControl?.setValue(1);
      }
    }
  }

  setPaymentBehavior(behavior: EasypassPaymentBehavior): void {
    this.form.patchValue({ paymentBehavior: behavior });
  }

  setAllowedEntries(entries: number): void {
    this.form.patchValue({ allowedEntries: entries });
  }

  isZoneSelected(zoneId: string | undefined): boolean {
    if (!zoneId) return false;
    const selected: string[] = this.form.get('selectedZones')?.value || [];
    return selected.includes(zoneId);
  }

  toggleZone(zoneId: string | undefined): void {
    if (!zoneId) return;
    const current: string[] = [...(this.form.get('selectedZones')?.value || [])];
    const index = current.indexOf(zoneId);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(zoneId);
    }
    this.form.patchValue({ selectedZones: current });
  }

  selectAllZones(): void {
    this.form.patchValue({ selectedZones: [] });
  }

  capacityDisplay(pass: EasypassDto): string {
    return pass.isUnlimited ? 'Unlimited vehicles' : `${pass.maxRedemptions ?? 0} vehicle${(pass.maxRedemptions ?? 0) === 1 ? '' : 's'}`;
  }

  entriesDisplay(pass: EasypassDto): string {
    const count = pass.allowedEntries ?? 0;
    if (count === 0) return 'Unlimited entries';
    if (count === 1) return 'Single use (1 entry)';
    return `${count} entries`;
  }

  paymentDisplay(pass: EasypassDto): string {
    return pass.paymentBehavior === EasypassPaymentBehavior.ParkerPays
      ? 'Visitor pays (Easypass tariff)'
      : 'Free for visitor (Billed to Org)';
  }

  zoneScopeDisplay(pass: EasypassDto): string {
    const zones = this.availableZones();
    const passZones = this.form.get('selectedZones')?.value || [];
    if (!zones.length || !passZones.length) {
      return 'All parking space zones';
    }
    const names = zones.filter(z => z.id && passZones.includes(z.id)).map(z => z.name || 'Zone');
    return names.length ? names.join(', ') : 'All zones';
  }

  toggleAdminSection(): void {
    this.showAdminSection.update(v => !v);
  }

  async copyLink(): Promise<void> {
    if (!this.redeemLink) return;
    try {
      await navigator.clipboard.writeText(this.redeemLink);
      this.copied.set(true);
      this.snackBar.open('Redeem link copied to clipboard', 'Close', { duration: 2500 });
      setTimeout(() => this.copied.set(false), 3000);
    } catch {
      this.snackBar.open('Failed to copy redeem link. Please select and copy manually.', 'Close', { duration: 3000 });
    }
  }

  async copyToken(token: string | null | undefined): Promise<void> {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      this.tokenCopied.set(true);
      this.snackBar.open('Token copied to clipboard', 'Close', { duration: 2000 });
      setTimeout(() => this.tokenCopied.set(false), 2500);
    } catch {
      this.snackBar.open('Could not copy token', 'Close', { duration: 2000 });
    }
  }

  downloadQrPng(): void {
    const root = this.host.nativeElement;
    const canvas = root.querySelector('canvas') as HTMLCanvasElement | null;
    const img = root.querySelector('img') as HTMLImageElement | null;

    const dataUrl = canvas ? canvas.toDataURL('image/png') : img?.src;
    if (!dataUrl) {
      this.downloadError.set('The QR code is still rendering. Please try again.');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = dataUrl;
      const pass = this.issued();
      const base = (pass?.title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      link.download = base ? `easypass-${base}.png` : `easypass-${pass?.token ?? 'invitation'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.downloadError.set(null);
      this.snackBar.open('QR code PNG downloaded', 'Close', { duration: 2500 });
    } catch {
      this.downloadError.set('Could not save the image. Right-click QR to save manually.');
    }
  }

  /**
   * Looks up the plate's running stay in this space and, when there is one, pulls the pass's start
   * back to the moment the car actually drove in.
   *
   * Without this the visitor is billed short-term for the gap between entry and the operator getting
   * to them — the exact minutes the pass is meant to cover. The value is only ever prefilled, never
   * locked: the operator may know the pass should start later (a stay that began on someone else's
   * business), and they can see the car.
   */
  onLicensePlateBlur(): void {
    // Canonical form, matching what the ANPR read stored. The API normalizes on binding too, so
    // this is not what makes the lookup work — it is what makes the value sent, the value shown on
    // the confirmation, and the value compared here all the same string.
    const plate = normalizePlate(this.form.get('licensePlate')?.value);
    const spaceId = this.parkingSpace?.id;

    this.activeStay.set(null);
    if (!plate || !spaceId) return;

    this.plateLookupLoading.set(true);
    this.parkingProcessService.parkingProcessByLicensePlatePost({
      licensePlateNumber: plate,
      parkingSpaceId: spaceId,
      activeOnly: true,
      pageIndex: 0,
      pageSize: 5
    }).subscribe({
      next: result => {
        const stay = (result?.items ?? []).find(p => p.isActive) ?? null;
        this.activeStay.set(stay);
        this.plateLookupLoading.set(false);

        if (stay?.startedAt) {
          this.form.patchValue({ validFrom: this.toDatetimeLocal(stay.startedAt) });
        }
      },
      // A failed lookup must not block issuing the pass — it only costs the prefill. Staying silent
      // would be wrong though: the operator would read the empty result as "no running stay" and
      // leave the entry minutes billed short-term.
      error: () => {
        this.plateLookupLoading.set(false);
        this.snackBar.open(this.translate.instant('easypass.lookupFailed'), 'Close', { duration: 5000 });
      }
    });
  }

  /** ISO instant → the local `YYYY-MM-DDTHH:mm` that `<input type="datetime-local">` accepts. */
  private toDatetimeLocal(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /** How long the car has already been inside, for the "already parked" hint. */
  activeStaySince(): string {
    const startedAt = this.activeStay()?.startedAt;
    return startedAt ? new Date(startedAt).toLocaleString() : '';
  }

  resetForNew(): void {
    this.issued.set(null);
    this.copied.set(false);
    this.tokenCopied.set(false);
    this.downloadError.set(null);
    this.activeStay.set(null);
    this.form.reset({
      licensePlate: '',
      title: '',
      description: '',
      maxRedemptions: 1,
      isUnlimited: false,
      allowedEntries: 1,
      organizationId: null,
      paymentBehavior: EasypassPaymentBehavior.OrgPays,
      validFrom: null,
      validTo: null,
      increaseUntil: null,
      selectedZones: [],
      costCenterId: null,
      email: '',
      phone: '',
      comment: '',
      validUntil: null,
      codeCount: null
    });
    this.form.get('maxRedemptions')?.enable();
    this.form.get('increaseUntil')?.enable();
    // Re-applies the mode's defaults and validators — a bare reset() leaves the previous mode's
    // required fields in force over fields the next issue will not show.
    this.setMode(this.modes[0] ?? 'simple');
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open(
        this.mode() === 'simple'
          ? this.translate.instant('easypass.licensePlateRequired')
          : this.mode() === 'exitTickets'
            ? this.translate.instant('easypass.exitTicketFieldsRequired')
            : 'Please fill in required fields (Title).',
        'Close', { duration: 3000 });
      return;
    }

    if (this.mode() === 'simple') {
      this.submitSimple();
      return;
    }

    if (this.mode() === 'exitTickets') {
      this.submitExitTickets();
      return;
    }

    this.isLoading.set(true);
    const v = this.form.getRawValue();
    const isUnlimited = !!v.isUnlimited;

    const request: CreateEasypassRequest = {
      operatorId: this.data.parkingOperatorId ?? null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : [],
      availableForParkingZones: v.selectedZones?.length ? v.selectedZones : [],
      organizationId: v.organizationId,
      validFrom: v.validFrom ? new Date(v.validFrom).toISOString() : null,
      validTo: v.validTo ? new Date(v.validTo).toISOString() : null,
      availableFrom: v.validFrom ? new Date(v.validFrom).toISOString() : null,
      availableTo: v.validTo ? new Date(v.validTo).toISOString() : null,
      increaseUntil: v.increaseUntil && !isUnlimited ? new Date(v.increaseUntil).toISOString() : null,
      maxRedemptions: isUnlimited ? 0 : (v.maxRedemptions ?? 1),
      isUnlimited,
      allowedEntries: v.allowedEntries ?? 0,
      title: v.title?.trim() || null,
      description: v.description?.trim() || null,
      costCenterId: v.costCenterId || null,
      email: v.email?.trim() || null,
      phone: v.phone?.trim() || null,
      comment: v.comment?.trim() || null,
      paymentBehavior: v.paymentBehavior ?? EasypassPaymentBehavior.OrgPays
    };

    this.parkingTypeService.parkingTypesEasypassesPost(request).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        this.issued.set(result);
        this.snackBar.open('Easypass invitation created successfully!', 'Close', { duration: 3000 });
      },
      error: (error) => {
        this.isLoading.set(false);
        // Long enough to actually read: the useful failures here name a space or a date range.
        this.snackBar.open(
          apiErrorMessage(error, 'Failed to create invitation'), 'Close', { duration: 10000 });
      }
    });
  }

  /**
   * The desk path: one call that creates the pass AND redeems it for the plate that was typed.
   *
   * It is deliberately not the two-step create-then-hand-over-a-QR flow. The visitor is at the desk
   * with their car, so there is nobody to send to the parker app — and a pass left unredeemed would
   * not open the gate for them. The backend does both halves, and rolls the invitation back if the
   * redemption fails, so a half-issued pass cannot survive.
   */
  private submitSimple(): void {
    this.isLoading.set(true);
    const v = this.form.getRawValue();

    const request: IssueEasypassForPlateRequest = {
      licensePlate: normalizePlate(v.licensePlate),
      operatorId: this.data.parkingOperatorId ?? null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : [],
      organizationId: v.organizationId || null,
      validFrom: v.validFrom ? new Date(v.validFrom).toISOString() : null,
      validTo: v.validTo ? new Date(v.validTo).toISOString() : null,
      costCenterId: v.costCenterId || null,
      note: v.comment?.trim() || null,
      // No title is asked for here; the backend names the pass after the plate so it stays findable
      // in the invitation list.
      title: null
    };

    this.parkingTypeService.parkingTypesEasypassesIssueForPlatePost(request).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        this.issued.set(result);
        this.snackBar.open(
          this.translate.instant('easypass.createdFor', { plate: request.licensePlate }),
          'Close', { duration: 4000 });
      },
      error: (error) => {
        this.isLoading.set(false);
        this.snackBar.open(
          apiErrorMessage(error, this.translate.instant('easypass.createFailed')), 'Close', { duration: 10000 });
      }
    });
  }

  /**
   * Prints N single-use exit tickets. Everything but the title, the last day and the count is fixed
   * by the backend: one free exit per code, all zones, no start, billed to the space's organization —
   * which is why no organization is asked for here.
   */
  private submitExitTickets(): void {
    this.isLoading.set(true);
    const v = this.form.getRawValue();

    const request: IssueEasypassExitTicketsRequest = {
      operatorId: this.data.parkingOperatorId ?? null,
      parkingSpaceId: this.parkingSpace.id!,
      title: v.title?.trim() ?? '',
      // <input type="date"> already yields "YYYY-MM-DD"; the backend reads it as a day in the site's time zone.
      validUntil: v.validUntil,
      codeCount: Number(v.codeCount),
      note: v.comment?.trim() || null
    };

    this.parkingTypeService.parkingTypesEasypassesExitTicketsPost(request).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        this.issued.set(result);
        this.snackBar.open(
          this.translate.instant('easypass.ticketsCreated', { count: result?.codeCount ?? request.codeCount }),
          'Close', { duration: 4000 });
      },
      error: (error) => {
        this.isLoading.set(false);
        this.snackBar.open(
          apiErrorMessage(error, this.translate.instant('easypass.createFailed')), 'Close', { duration: 10000 });
      }
    });
  }

  /** Opens the print sheet for an exit-ticket pass. */
  openPrint(pass: EasypassDto): void {
    openExitTicketPrint(this.dialog, pass, this.data.parkingOperatorId, this.parkingSpace.timeZoneId);
  }

  /** The plate the desk pass was issued for — the form still holds it after a successful issue. */
  issuedPlate(): string {
    return normalizePlate(this.form.get('licensePlate')?.value);
  }

  readonly maxPlateLength = MAX_PLATE_LENGTH;

  /**
   * Rejects characters a plate can never contain (`;`, `[`, quotes, emoji …) as they are typed or
   * pasted, and clamps the length. The caret is restored by hand so a stray character typed
   * mid-plate does not throw the operator to the end of the field. Separators and case are left
   * alone here — this is the input filter, not the canonical form; normalizePlate does that when
   * the value is looked up or sent.
   */
  onLicensePlateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    const cleaned = sanitizePlateInput(raw);
    if (cleaned === raw) {
      return;
    }

    const caret = input.selectionStart ?? raw.length;
    const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
    this.form.get('licensePlate')!.setValue(cleaned);
    input.value = cleaned;
    const restored = Math.max(0, caret - rejectedBeforeCaret);
    input.setSelectionRange(restored, restored);
  }

  /** The billed organization's name, for the desk confirmation. Empty when it is the operator's own. */
  issuedOrganizationName(): string {
    const id = this.form.get('organizationId')?.value;
    return this.organizations().find(o => o.id === id)?.name ?? '';
  }

  close(): void {
    this.dialogRef.close(!!this.issued());
  }
}
